import React, { useState, useRef, useEffect } from 'react';
import { ChatOpenAI } from "@langchain/openai";
import { DynamicTool } from "@langchain/core/tools";
import { ChatPromptTemplate } from "@langchain/core/prompts";
import { AIMessage, HumanMessage, SystemMessage, ToolMessage } from "@langchain/core/messages";
import authService from '../services/authService';
import { API_ROOT, OPENAI_PROXY_BASE_URL } from './api';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';



// ---------- hard caps to prevent token explosions ----------
const MAX_HISTORY_MSGS = 5;            // only keep this many previous UI messages in-context
const MAX_DB_ROWS = 200;                // send at most this many rows from DB to the model
const MAX_TOOL_RESULT_CHARS = 15000;    // hard character cap for any tool result
const MAX_DOC_CHARS = 8000;             // cap for doc context sent to LLM
const MAX_DB_BROADEN_RETRIES = 2;       // extra retries when DB query returns no rows

function truncateText(s, max) {
  if (!s) return s;
  if (s.length <= max) return s;
  return s.slice(0, max) + `\n\n…[TRUNCATED to ${max.toLocaleString()} chars]`;
}

const TOOL_LABELS = {
  document_search: 'Document Search',
  database_query: 'Database Query',
};

function formatToolLabel(name) {
  return TOOL_LABELS[name] || String(name || '').replace(/_/g, ' ');
}

function extractDocumentSources(resultText) {
  if (!resultText || resultText === 'NO_RESULTS_FOUND') return [];
  const sources = [];
  const re = /^Source\s*\d+\s*:\s*(.+)$/gm;
  let match;
  while ((match = re.exec(resultText)) !== null) {
    const name = (match[1] || '').trim();
    if (name && name !== 'Unknown' && !sources.includes(name)) {
      sources.push(name);
    }
  }
  return sources;
}

const CHATBOT_LOG_PREVIEW_CHARS = 800;

const SupportChatbotV2 = () => {
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem('thrive360-chatbot-open');
    return saved ? JSON.parse(saved) : false;
  });
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('thrive360-chatbot-expanded');
    return saved ? JSON.parse(saved) : false;
  });
  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem('thrive360-chatbot-messages');
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        return parsed.map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (error) {
        console.error('Error parsing saved messages:', error);
      }
    }
    return [
      { 
        sender: 'bot', 
        text: 'Hello! I\'m the THRIVE360 Support Assistant V2. I can help you with both documentation and data queries. How can I assist you today?',
        timestamp: new Date()
      }
    ];
  });
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [apiStatus, setApiStatus] = useState('Initializing system...');
  const [apiReady, setApiReady] = useState(false);
  const [currentTool, setCurrentTool] = useState(null);
  
  const messagesEndRef = useRef(null);
  const llmRef = useRef(null);
  const modelWithToolsRef = useRef(null);
  const toolsRef = useRef([]);

  const extractText = (msg) => {
    try {
      if (!msg) return '';
      const content = msg.content;
      if (typeof content === 'string') return content;
      if (Array.isArray(content)) {
        const parts = content
          .map((p) => {
            if (!p) return '';
            if (typeof p === 'string') return p;
            if (p.type === 'text' && typeof p.text === 'string') return p.text;
            if (typeof p.content === 'string') return p.content;
            return '';
          })
          .filter(Boolean);
        return parts.join('\n');
      }
      if (typeof content === 'object' && content !== null) {
        if (typeof content.text === 'string') return content.text;
        if (typeof content.content === 'string') return content.content;
      }
      return String(content ?? '');
    } catch (_) {
      return '';
    }
  };

  const sanitizeSqlFromModel = (text) => {
    if (!text) return '';
    const raw = String(text).trim();
    if (raw.startsWith('```')) {
      const stripped = raw
        .replace(/^```[a-zA-Z]*\s*/i, '')
        .replace(/\s*```$/, '')
        .trim();
      return stripped;
    }
    return raw;
  };

  const handleCopy = async (text) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(typeof text === 'string' ? text : String(text));
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const logChatbotTurn = async ({
    question,
    answer,
    toolsUsed,
    latencyMs,
    hadError,
    errorMessage
  }) => {
    try {
      const idToken = await authService.getIdToken();
      const baseUrl = API_ROOT;

      const response = await fetch(`${baseUrl}/logUserActivity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          action: 'CHATBOT_TURN',
          details: {
            question: truncateText(String(question || ''), CHATBOT_LOG_PREVIEW_CHARS),
            answerPreview: truncateText(String(answer || ''), CHATBOT_LOG_PREVIEW_CHARS),
            toolsUsed: Array.from(toolsUsed || []),
            latencyMs,
            hadError: !!hadError,
            errorMessage: hadError ? truncateText(String(errorMessage || ''), CHATBOT_LOG_PREVIEW_CHARS) : null,
            ts: new Date().toISOString()
          },
          userAgent: navigator?.userAgent || null
        })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`logUserActivity failed: ${response.status} ${text}`);
      }
    } catch (err) {
      console.error('Failed to log chatbot turn:', err);
    }
  };

  useEffect(() => {
    localStorage.setItem('thrive360-chatbot-messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('thrive360-chatbot-open', JSON.stringify(isOpen));
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem('thrive360-chatbot-expanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Initialize LLM and tools
  useEffect(() => {
    const setupSystem = async () => {
      try {
        const openAiModel = process.env.REACT_APP_OPENAI_MODEL || "gpt-4o-mini";

        llmRef.current = new ChatOpenAI({
          model: openAiModel,
          temperature: 0.7,
          apiKey: "proxy",
          configuration: {
            baseURL: OPENAI_PROXY_BASE_URL,
            dangerouslyAllowBrowser: true,
            fetch: async (url, init = {}) => {
              const idToken = await authService.getIdToken();
              const headers = new Headers(init.headers || {});
              headers.set("Authorization", `Bearer ${idToken}`);
              return fetch(url, { ...init, headers });
            },
          },
        });

        const tools = [
          // eslint-disable-next-line
          new DynamicTool({
            name: "document_search",
            description: "Search through documentation and knowledge base. Input should be a question or topic to search for.",
            func: async (input) => {
              try {
                setCurrentTool("document_search");
                const idToken = await authService.getIdToken();
                const response = await fetch(`${API_ROOT}/doc-search`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    Authorization: `Bearer ${idToken}`
                  },
                  body: JSON.stringify({ query: input, n_results: 2 }),
                });
                
                if (!response.ok) {
                  const errorText = await response.text();
                  console.error('Document search failed:', errorText);
                  throw new Error(`Document search failed: ${response.status} ${response.statusText}`);
                }

                const data = await response.json();
                console.log("Document search data:", data);
                
                if (!data.results || data.results.length === 0) {
                  return "NO_RESULTS_FOUND";
                }

                // Return retrieved chunks only. The main chat loop writes the user-facing answer,
                // the same way database_query returns rows instead of a finished reply.
                const docContext = data.results.map((result, idx) => {
                  const filename = result?.metadata?.filename || 'Unknown';
                  const content = result?.content || '';
                  return `Source ${idx + 1}: ${filename}\n${content}`;
                }).join("\n\n---\n\n");
                return truncateText(docContext, MAX_DOC_CHARS);
              } catch (error) {
                console.error('Document search error:', error);
                throw error;
              } finally {
                setCurrentTool(null);
              }
            },
          }),
          // eslint-disable-next-line
          new DynamicTool({
            name: "database_query",
            description: `Query the MySQL database for immunization stock data. Pass a single SQL statement; no code fences or JSON. Prefer including LIMIT unless the user asks for full data.
              Available tables/columns:
              - stores(iso3, SC_Level_No, Store_Name)
              - vw_ri_stocks(Report_Date, iso3, SC_Level_No, Store_Name, Antigen_Name, Doses) Antigen Names always use MySQL LIKE '%NAME%'
              Always fully-qualify columns when joining.`,
            func: async (input) => {
              try {
                setCurrentTool("database_query");
                const idToken = await authService.getIdToken();
                const response = await fetch(`${API_ROOT}/db-query`, {
                  method: 'POST',
                  headers: { 
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${idToken}`
                  },
                  body: JSON.stringify({ 
                    query: input,
                    tables: ['stores', 'ri_stocks']
                  }),
                });
                
                if (!response.ok) {
                  let errorMsg = 'Database query failed';
                  try {
                    const errorData = await response.json();
                    if (errorData?.error) errorMsg = errorData.error;
                  } catch (_) {}
                  throw new Error(errorMsg);
                }
                
                const data = await response.json();
                const rows = Array.isArray(data.result) ? data.result : [];
                if (rows.length === 0) return "NO_RESULTS_FOUND";
                const hasMeaningfulData = rows.some((row) =>
                  Object.values(row || {}).some((v) => {
                    if (v === null || v === undefined) return false;
                    if (typeof v === 'string' && v.trim() === '') return false;
                    return true;
                  })
                );
                if (!hasMeaningfulData) return "NO_RESULTS_FOUND";

                const total = rows.length;
                const limited = rows.slice(0, MAX_DB_ROWS);
                let body = limited.map(row =>
                  Object.entries(row)
                    .map(([k, v]) => `${k}: ${v === null ? 'NULL' : v}`)
                    .join('\n')
                ).join('\n\n');

                body = truncateText(body, MAX_TOOL_RESULT_CHARS);

                const footer = total > MAX_DB_ROWS
                  ? `\n\n…[SHOWING FIRST ${MAX_DB_ROWS} OF ${total} ROWS]`
                  : '';
                return body + footer;
              } catch (error) {
                console.error('Database query error:', error);
                throw error;
              } finally {
                setCurrentTool(null);
              }
            },
          })
        ];

        modelWithToolsRef.current = llmRef.current.bindTools(tools);
        toolsRef.current = tools;

        setApiStatus("System initialized successfully");
        setApiReady(true);
        console.log("System is ready");
      } catch (error) {
        console.error("Error setting up system:", error);
        setApiStatus(`Error: ${error.message}`);
      }
    };

    setupSystem();
  }, []);

  const clearChat = () => {
    const initialMessage = {
      sender: 'bot', 
      text: 'Hello! I\'m the THRIVE360 Support Assistant V2. I can help you with both documentation and data queries. How can I assist you today?',
      timestamp: new Date()
    };
    setMessages([initialMessage]);
  };

  const handleSendMessage = async () => {
    if (input.trim() === '') return;
    
    const userMessage = {
      sender: 'user',
      text: input,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    const startedAt = Date.now();
    const toolsUsedThisTurn = new Set();
    const documentSourcesThisTurn = [];
    
    try {
      // Keep only the last N UI messages to control prompt size
      const history = messages.slice(-MAX_HISTORY_MSGS);

      const system = new SystemMessage(
        "You are a helpful assistant for the THRIVE360 Suite. You have access to documentation and a database. " +
        "If a tool returns \"NO_RESULTS_FOUND\", say you couldn't find any data. " +
        "When document_search returns source chunks, answer from those chunks and name the source documents. Do not invent content. " +
        "When database_query returns rows, summarize them for the user. " +
        "When writing SQL, include ORDER BY when relevant and add LIMIT 200 unless the user asks for the full dataset."
      );

      const prior = history
        .filter(m => typeof m.text === 'string')
        .map(msg => (msg.sender === 'user' ? new HumanMessage(msg.text ?? "") : new AIMessage(msg.text ?? "")));
      const convo = [system, ...prior, new HumanMessage(userMessage.text)];

      let outputText = "";
      let iterations = 0;

      while (iterations < 6) {
        iterations += 1;
        console.log(`[Chatbot] Iteration ${iterations}: invoking model with ${convo.length} messages...`);
        let aiMsg;
        try {
          aiMsg = await modelWithToolsRef.current.invoke(convo);
        } catch (err) {
          // Surface rate-limit / size issues clearly in UI
          const msg = (err && err.message) ? String(err.message) : String(err);
          console.error("LLM invoke error:", msg);
          if (msg.includes("TPM") || msg.includes("tokens per min") || msg.includes("Request too large")) {
            throw new Error("The request exceeded token limits. I trimmed history and tool outputs. Please try again.");
          }
          throw err;
        }

        console.log('[Chatbot] AI message raw:', aiMsg);
        convo.push(aiMsg);

        // Robust tool-call extraction (new LC shape and raw OpenAI shape)
        const toolCalls =
          aiMsg.tool_calls ??
          (aiMsg.additional_kwargs && aiMsg.additional_kwargs.tool_calls) ??
          [];

        console.log('[Chatbot] toolCalls:', toolCalls);

        if (!toolCalls || toolCalls.length === 0) {
          outputText = extractText(aiMsg);
          console.log('[Chatbot] Final model outputText:', outputText);
          break;
        }

        for (const call of toolCalls) {
          // Prefer new LC normalized shape; fallback to raw OpenAI
          const toolName = (call && call.name) || (call && call.function && call.function.name);
          let args = (call && call.args) || (call && call.function && call.function.arguments);
          if (toolName) toolsUsedThisTurn.add(toolName);

          if (typeof args === 'string') {
            try { args = JSON.parse(args); } catch (_) {}
          }

          const inputForTool =
            args && typeof args === 'object' && Object.prototype.hasOwnProperty.call(args, 'input')
              ? args.input
              : args;

          console.log('[Chatbot] Executing tool:', toolName, 'args:', inputForTool);

          const tool = (toolsRef.current || []).find(t => t.name === toolName);
          const toolResult = tool
            ? await tool.invoke(inputForTool)
            : `Tool ${toolName} not found.`;

          let resultText = typeof toolResult === "string" ? toolResult : String(toolResult ?? "");
          resultText = truncateText(resultText, MAX_TOOL_RESULT_CHARS);
          console.log('[Chatbot] Tool result:', resultText && resultText.slice ? resultText.slice(0, 500) : resultText);

          if (toolName === "document_search") {
            extractDocumentSources(resultText).forEach((name) => {
              if (!documentSourcesThisTurn.includes(name)) documentSourcesThisTurn.push(name);
            });
          }

          if (toolName === "database_query" && resultText === "NO_RESULTS_FOUND") {
            let currentSql = typeof inputForTool === 'string' ? inputForTool : String(inputForTool ?? '');
            let fallbackSummary = '';

            for (let retry = 1; retry <= MAX_DB_BROADEN_RETRIES; retry += 1) {
              try {
                const broadenPrompt = ChatPromptTemplate.fromMessages([
                  ["system",
                    "You rewrite SQL to be broader when no rows are returned. " +
                    "Return only one MySQL SELECT statement. No markdown, no explanation. " +
                    "Keep tables limited to stores and ri_stocks. Keep LIMIT 200 unless already lower. " +
                    "Broaden only one constraint for this retry using this order: widen date range, relax exact text match to LIKE, remove least-critical filter."
                  ],
                  ["human",
                    "User question:\n{question}\n\n" +
                    "Failed SQL:\n{failedSql}\n\n" +
                    "Retry number: {retry}\n\n" +
                    "Return only broader SQL."
                  ]
                ]);

                const broadenMsgs = await broadenPrompt.formatMessages({
                  question: userMessage.text,
                  failedSql: currentSql,
                  retry: String(retry),
                });
                const broadenResp = await llmRef.current.invoke(broadenMsgs);
                const broaderSql = sanitizeSqlFromModel(extractText(broadenResp));
                if (!broaderSql) break;
                console.log(`[Chatbot] DB broaden retry ${retry}: SQL:`, broaderSql);

                const retryResult = await tool.invoke(broaderSql);
                let retryText = typeof retryResult === "string" ? retryResult : String(retryResult ?? "");
                retryText = truncateText(retryText, MAX_TOOL_RESULT_CHARS);
                console.log(`[Chatbot] DB broaden retry ${retry}: result:`, retryText && retryText.slice ? retryText.slice(0, 500) : retryText);

                if (retryText !== "NO_RESULTS_FOUND") {
                  fallbackSummary =
                    `QUERY_PROVENANCE: Initial SQL returned no rows. ` +
                    `A broader retry (attempt ${retry}) was executed to relax constraints and produced the final result.\n` +
                    `INITIAL_SQL: ${currentSql}\n` +
                    `BROADENED_SQL: ${broaderSql}\n\n`;
                  resultText = fallbackSummary + retryText;
                  break;
                }

                currentSql = broaderSql;
              } catch (retryErr) {
                console.error('Database broaden retry error:', retryErr);
                break;
              }
            }
          }

          convo.push(
            new ToolMessage({
              content: resultText,
              tool_call_id: (call && call.id) || undefined,
              name: toolName
            })
          );
        }
      }

      if (!outputText || String(outputText).trim() === '') {
        outputText = "I couldn't produce an answer right now. Please try rephrasing your question or try again.";
      }

      const botMessage = {
        sender: 'bot',
        text: outputText,
        timestamp: new Date(),
        toolsUsed: Array.from(toolsUsedThisTurn),
        documentSources: documentSourcesThisTurn
      };
      
      setMessages(prev => [...prev, botMessage]);
      void logChatbotTurn({
        question: userMessage.text,
        answer: outputText,
        toolsUsed: toolsUsedThisTurn,
        latencyMs: Date.now() - startedAt,
        hadError: false
      });
    } catch (error) {
      console.error('Error in chat:', error);
      
      const errorMessage = {
        sender: 'bot',
        text: (error && error.message)
          ? error.message
          : "I'm having trouble processing your request. Please try again.",
        timestamp: new Date(),
        toolsUsed: Array.from(toolsUsedThisTurn),
        documentSources: documentSourcesThisTurn
      };
      
      setMessages(prev => [...prev, errorMessage]);
      void logChatbotTurn({
        question: userMessage.text,
        answer: '',
        toolsUsed: toolsUsedThisTurn,
        latencyMs: Date.now() - startedAt,
        hadError: true,
        errorMessage: error?.message || String(error)
      });
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50">
      {/* Chat toggle button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center ${isOpen ? 'bg-red-500' : 'bg-blue-600'} text-white`}
      >
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>
      
      {/* Chat window */}
      {isOpen && (
        <div className={`absolute bottom-16 right-0 ${isExpanded ? 'w-[800px] h-[600px]' : 'w-80 sm:w-96 h-96'} bg-white rounded-lg shadow-xl flex flex-col overflow-hidden border border-gray-300 transition-all duration-300`}>
          {/* Chat header */}
          <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center">
              <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center mr-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium">THRIVE360 Support V2</h3>
                <p className="text-xs text-blue-100">AI Assistant with Data Access</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={clearChat}
                className="p-1 rounded-full hover:bg-blue-700 transition-colors"
                title="Clear Chat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 rounded-full hover:bg-blue-700 transition-colors"
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {!apiReady && (
            <div className="bg-blue-50 px-4 py-2 text-xs text-blue-800">
              {apiStatus}
              <div className="w-full mt-1 h-1 bg-blue-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          )}
          
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`mb-3 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`relative px-4 py-3 pr-8 rounded-lg max-w-[80%] ${
                    message.sender === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-none' 
                      : 'bg-white text-gray-800 shadow-sm rounded-bl-none'
                  }`}
                >
                  <button
                    onClick={() => handleCopy(message.text)}
                    className={`absolute top-2 right-2 p-1 rounded transition-colors ${
                      message.sender === 'user' ? 'hover:bg-blue-700 text-blue-100' : 'hover:bg-gray-100 text-gray-500'
                    }`}
                    title="Copy"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5A3.375 3.375 0 0 0 6.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0 0 15 2.25h-1.5a2.251 2.251 0 0 0-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 0 0-9-9Z" />
                    </svg>
                  </button>
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({node, ...props}) => <p className="text-sm whitespace-pre-line" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc pl-4" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal pl-4" {...props} />,
                        li: ({node, ...props}) => <li className="text-sm" {...props} />,
                        table: ({node, ...props}) => <table className="border-collapse border border-gray-300" {...props} />,
                        th: ({node, ...props}) => <th className="border border-gray-300 px-2 py-1 bg-gray-100" {...props} />,
                        td: ({node, ...props}) => <td className="border border-gray-300 px-2 py-1" {...props} />,
                        code: ({node, inline, ...props}) => 
                          inline ? (
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props} />
                          ) : (
                            <pre className="bg-gray-100 p-2 rounded overflow-x-auto">
                              <code className="text-sm" {...props} />
                            </pre>
                          ),
                        blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic" {...props} />,
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  </div>
                  <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {new Date(message.timestamp).toLocaleString([], {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                    {message.sender === 'bot' && Array.isArray(message.toolsUsed) && message.toolsUsed.length > 0 && (
                      <>
                        {' | '}
                        {message.toolsUsed.map(formatToolLabel).join(', ')}
                        {Array.isArray(message.documentSources) && message.documentSources.length > 0 && (
                          <> | {message.documentSources.join(', ')}</>
                        )}
                      </>
                    )}
                  </p>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start mb-3">
                <div className="bg-white text-gray-800 px-4 py-3 rounded-lg rounded-bl-none shadow-sm max-w-[80%]">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                    </div>
                    {currentTool && (
                      <span className="text-xs text-gray-500">
                        Using {currentTool === 'document_search' ? 'document search' : 'database query'}...
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Chat input */}
          <div className="border-t border-gray-200 p-3 flex">
            <input
              type="text"
              name="thrive360-chatbot-message"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask about THRIVE360 or query data..."
              className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!apiReady || isTyping}
            />
            <button
              onClick={handleSendMessage}
              disabled={input.trim() === '' || isTyping || !apiReady}
              className={`px-4 py-2 rounded-r-lg ${
                input.trim() === '' || isTyping || !apiReady
                  ? 'bg-gray-300 text-gray-500' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportChatbotV2;
